import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import type { HomeResponse } from '../types';
import Rail, { Hero } from '../components/Rail';

export default function HomePage() {
  const [data, setData] = useState<HomeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const res = await api.home();
      setData(res);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // While media is still processing, poll so the library fills in live.
    const timer = data && data.stats.pending > 0 ? setInterval(() => void load(), 3000) : undefined;
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [data?.stats.pending]);

  if (loading) return <HomeSkeleton />;

  if (error) {
    return (
      <EmptyState
        title="Can't reach the server"
        body={error}
        action={
          <Link to="/import" className="rounded-md bg-accent px-5 py-2.5 text-sm font-semibold">
            Go to Upload
          </Link>
        }
      />
    );
  }

  const sharedRails = (data?.shared ?? []).map((rail) => (
    <Rail
      key={rail.key}
      title={rail.title}
      subtitle={rail.seasonYear ? 'Shared with you' : undefined}
      episodes={rail.episodes}
      viewAllTo={rail.seasonYear ? `/season/${rail.seasonYear}?owner=${rail.owner.id}` : null}
    />
  ));

  // Nothing of their own yet, but someone shared memories: show those first.
  if (data && !data.hasAnyMedia && sharedRails.length > 0) {
    return (
      <div className="pt-8">
        {data.continueWatching.length > 0 && (
          <Rail title="Continue Watching" episodes={data.continueWatching} showProgress viewAllTo={null} />
        )}
        {sharedRails}
        <div className="mx-4 rounded-xl bg-white/[0.03] p-5 ring-1 ring-white/10 sm:mx-6 lg:mx-10">
          <p className="text-sm text-white/70">Want your own episodes too?</p>
          <Link to="/import" className="mt-3 inline-block rounded-md bg-accent px-4 py-2 text-sm font-semibold transition hover:bg-accent-soft">
            Upload your memories
          </Link>
        </div>
      </div>
    );
  }

  if (!data || !data.hasAnyMedia) {
    return (
      <EmptyState
        title="Your story starts here"
        body="Upload photos and videos. Memoryflix reads their dates and locations, groups them into events, and turns them into seasons and episodes you can play like a show."
        action={
          <Link to="/import" className="rounded-md bg-accent px-5 py-2.5 text-sm font-semibold transition hover:bg-accent-soft">
            Upload your first memories
          </Link>
        }
      />
    );
  }

  return (
    <div>
      <Hero episode={data.hero} stats={data.stats} />

      {data.continueWatching.length > 0 && (
        <Rail
          title="Continue Watching"
          episodes={data.continueWatching}
          showProgress
        />
      )}

      {data.rails.map((season) => (
        <Rail
          key={season.id}
          title={season.title}
          subtitle={season.description}
          episodes={season.episodes ?? []}
        />
      ))}

      {sharedRails}

      {data.stats.failed > 0 && (
        <div className="mx-4 mb-10 rounded-lg border border-amber-500/25 bg-amber-500/5 p-4 sm:mx-6 lg:mx-10">
          <p className="text-sm text-amber-200/90">
            <strong>{data.stats.failed}</strong> file{data.stats.failed === 1 ? '' : 's'} could not be
            processed. Check the Library page for details.
          </p>
        </div>
      )}
    </div>
  );
}

function HomeSkeleton() {
  return (
    <div>
      <div className="skeleton h-[62vh] min-h-[420px] w-full rounded-none" />
      {[0, 1].map((i) => (
        <div key={i} className="mb-10 px-4 sm:px-6 lg:px-10">
          <div className="skeleton mb-3 h-6 w-40" />
          <div className="flex gap-4">
            {[0, 1, 2, 3, 4].map((j) => (
              <div key={j} className="skeleton aspect-video w-[clamp(200px,26vw,300px)]" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ title, body, action }: { title: string; body: string; action?: React.ReactNode }) {
  return (
    <div className="mx-auto flex min-h-[70vh] max-w-xl flex-col items-center justify-center px-6 text-center">
      <h1 className="mb-3 text-3xl font-extrabold tracking-tight sm:text-4xl">{title}</h1>
      <p className="mb-8 text-sm leading-relaxed text-white/55 sm:text-base">{body}</p>
      {action}
    </div>
  );
}